import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import salaryService from '../../services/SalaryService.js';
import axiosInstance from '../../config/axios.js';
import { useAuth } from '../../context/AuthContext.js';
import { Wallet, Pencil, ArrowLeft, Download } from "lucide-react";
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';
import { formatUsernameLocation } from '../../utils/userDisplay';


const StatBox = ({ label, value, color = 'border-gray-300', valueClassName = 'text-gray-900' }) => (
  <div className={`bg-white rounded-xl shadow-sm p-3 sm:p-4 border border-gray-100 border-l-4 ${color} w-full min-w-0`}>
    <p className="text-[11px] sm:text-xs text-gray-600 font-semibold mb-1 leading-snug">{label}</p>
    <p className={`text-sm sm:text-base xl:text-lg font-bold break-words leading-tight ${valueClassName}`}>{value}</p>
  </div>
);

const ViewSalary = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const canManage = ['System Admin', 'Admin', 'Accountant'].includes(currentUser?.role);
  const { salaryId } = useParams();
  const [salary, setSalary] = useState(null);
  const [previousSalaries, setPreviousSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllPrevious, setShowAllPrevious] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  useEffect(() => {
    fetchSalaryDetails();
  }, [salaryId]);

  const fetchSalaryDetails = async () => {
    try {
      const [salaryRes, userSalariesRes] = await Promise.all([
        salaryService.getById(salaryId),
        getSalaryUserId()
      ]);
      
      setSalary(salaryRes.data);
      
      // Fetch all salaries for this user
      if (salaryRes.data.user_id) {
        const allSalariesRes = await salaryService.getByUserId(salaryRes.data.user_id);
        const otherSalaries = allSalariesRes.data.filter(s => s.salary_id !== salaryId);
        setPreviousSalaries(otherSalaries);
      }
    } catch (err) {
      Alert.error('Failed to load salary details');
      console.error(err);
      navigate('/salaries');
    } finally {
      setLoading(false);
    }
  };

  const getSalaryUserId = async () => {
    const response = await salaryService.getById(salaryId);
    return response.data.user_id;
  };

  const getSalaryPeriod = () => {
    if (!salary) return null;
    if (salary.month && salary.year) {
      return {
        month: String(salary.month).toLowerCase(),
        year: String(salary.year),
      };
    }
    if (salary.month_year) {
      const [monthName, year] = salary.month_year.split(' ');
      return { month: monthName.toLowerCase(), year };
    }
    return null;
  };

  const handleDownloadSalarySlip = async () => {
    try {
      const period = getSalaryPeriod();
      if (!period) {
        Alert.error('Unable to determine salary period for download');
        return;
      }

      const blob = await salaryService.downloadSalarySlipFile(
        salary.user_id,
        period.month,
        period.year
      );
      const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `salary_slip_${salary.user_id}_${period.month}_${period.year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      Alert.success('Salary slip downloaded successfully!');
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Failed to download salary slip');
      console.error('Download error:', err);
    }
  };

  const viewPaymentScreenshot = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const token = user.token;
      
      if (!token) {
        Alert.error('Authentication required. Please login again.');
        return;
      }
      
      // Construct screenshot URL
      const screenshotUrl = salaryService.getPaymentScreenshot(salaryId);
      
      // Fetch with authorization header
      const response = await fetch(screenshotUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load payment screenshot');
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create a temporary URL for the blob and open in new tab
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Optional: Clean up after a delay (browser will handle this anyway)
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
      
    } catch (err) {
      Alert.error(err.message || 'Failed to view payment screenshot');
      console.error('Screenshot view error:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Math.round(amount || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      credited: 'bg-green-100 text-green-800 border-green-300',
      processing: 'bg-blue-100 text-blue-800 border-blue-300',
      hold: 'bg-orange-100 text-orange-800 border-orange-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const calculateTotalEarnings = () => {
    if (!salary) return 0;
    return (salary.basic_salary || 0) + (salary.allowances || 0) + (salary.bonus || 0) + (salary.overtime_pay || 0);
  };

  const calculatePreviousTotal = () => {
    return previousSalaries.reduce((sum, s) => sum + (s.net_salary || 0), 0);
  };

  const displayedPreviousSalaries = showAllPrevious ? previousSalaries : previousSalaries.slice(0, 3);

  const breakdownItems = salary
    ? [
        { key: 'basic', label: 'Basic Salary', value: formatCurrency(salary.basic_salary), color: 'border-green-500' },
        { key: 'allowances', label: 'Allowances', value: formatCurrency(salary.allowances), color: 'border-green-500' },
        { key: 'bonus', label: 'Bonus', value: formatCurrency(salary.bonus), color: 'border-green-500' },
        { key: 'overtime', label: 'Overtime Pay', value: formatCurrency(salary.overtime_pay), color: 'border-green-500' },
        { key: 'deductions', label: 'Deductions', value: formatCurrency(salary.deductions), color: 'border-red-500' },
        { key: 'total-earnings', label: 'Total Earnings', value: formatCurrency(calculateTotalEarnings()), color: 'border-green-500' },
        { key: 'total-deductions', label: 'Total Deductions', value: formatCurrency(salary.deductions), color: 'border-red-500' },
        { key: 'net-salary', label: 'Net Salary', value: formatCurrency(salary.net_salary), color: 'border-blue-500', valueClassName: 'text-blue-700' },
      ]
    : [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading salary details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!salary) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
            <Wallet className="w-6 h-6 sm:w-7 sm:h-7" />
            Salary Details
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">View complete salary record information</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button
            variant="primary"
            icon={Download}
            onClick={handleDownloadSalarySlip}
          >
            Download Salary Slip
          </Button>
          {canManage && (
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => navigate(`/salaries/edit/${salaryId}`)}
            >
              Edit Salary
            </Button>
          )}
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/salaries')}
          >
            Back to Salaries
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-orange-500 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Salary ID</p>
            <p className="text-sm sm:text-base xl:text-xl font-bold text-orange-600 font-mono break-all">
              {salary.salary_id}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-blue-500 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Status</p>
            <span className={`inline-block px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold uppercase border ${getStatusColor(salary.status)}`}>
              {salary.status}
            </span>
          </div>
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-green-500 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Net Salary</p>
            <p className="text-sm sm:text-base xl:text-xl font-bold text-green-600 break-words">
              {formatCurrency(salary.net_salary)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-purple-500 min-w-0">
            <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Period</p>
            <p className="text-sm sm:text-base xl:text-xl font-bold text-gray-800 break-words">
              {salary.month_year || `${salary.month_display} ${salary.year}`}
            </p>
            <p className="text-xs text-gray-500 mt-1 break-words">{salary.user_details?.name}</p>
          </div>
      </div>

        {/* Salary Breakdown */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
            Salary Breakdown
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 sm:gap-4">
              {breakdownItems.map(({ key, label, value, color, valueClassName }) => (
                <StatBox
                  key={key}
                  label={label}
                  value={value}
                  color={color}
                  valueClassName={valueClassName}
                />
              ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-6">
          {/* Employee Information */}
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5">
            <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Employee Information
            </h2>
            <div className="grid grid-cols-2 gap-2 sm:gap-5">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Employee Name</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {salary.user_details?.name}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Name / Location</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {formatUsernameLocation(
                    salary.user_details?.name || salary.user_details?.username,
                    salary.user_details?.location || salary.user_details?.city,
                    salary.user_id
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Email</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {salary.user_details?.email || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Role</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {salary.user_details?.role_name || 'N/A'}
                </p>
              </div>
              {salary.user_details?.city && (
                <div className="sm:col-span-2">
                  <p className="text-xs sm:text-sm text-gray-600 font-semibold">Location</p>
                  <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                    📍 {salary.user_details.city}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5">
            <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Payment Information
            </h2>
            <div className="grid grid-cols-2 gap-2 sm:gap-5">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Payment Mode</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {salary.payment_mode ? salary.payment_mode.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Transaction ID</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {salary.transaction_id || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Payment Date</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {formatDate(salary.payment_date)}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">Credited By</p>
                <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                  {salary.credited_by || 'N/A'}
                </p>
              </div>
              {salary.has_screenshot && (
                <div className="sm:col-span-2">
                  <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-2">Payment Screenshot</p>
                  <Button variant="secondary" onClick={viewPaymentScreenshot}>
                    View Payment Screenshot
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {salary.remarks && (
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Remarks
            </h2>
            <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-3 py-2">
              {salary.remarks}
            </p>
          </div>
        )}

        {/* System Information */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
            System Information
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-5">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 font-semibold">{t('common.createdBy')}</p>
              <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                {salary.created_by}
              </p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-600 font-semibold">Created At</p>
              <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                {formatDate(salary.created_at)}
              </p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-600 font-semibold">Last Updated</p>
              <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-all">
                {formatDate(salary.updated_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Previous Salary Records */}
        {previousSalaries.length > 0 && (
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-4 sm:mb-6">
            <div className="p-4 sm:p-5 border-b border-gray-200">
              <h2 className="text-base sm:text-md font-semibold text-secondary">
                Previous Salary Records ({previousSalaries.length})
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Total Previous Payments: {formatCurrency(calculatePreviousTotal())}
              </p>
            </div>

            <div className="p-4 sm:p-5">
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full table-fixed border-collapse">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Period</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Basic</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Allowances</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Deductions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Net Salary</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {displayedPreviousSalaries.map((prevSalary, index) => (
                      <tr
                        key={prevSalary.salary_id}
                        className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className="px-5 py-3 text-sm font-regular text-gray-900">{prevSalary.month_year}</td>
                        <td className="px-5 py-3 text-sm font-regular text-gray-900">{formatCurrency(prevSalary.basic_salary)}</td>
                        <td className="px-5 py-3 text-sm font-regular text-gray-900">{formatCurrency(prevSalary.allowances)}</td>
                        <td className="px-5 py-3 text-sm font-regular text-gray-900">{formatCurrency(prevSalary.deductions)}</td>
                        <td className="px-5 py-3 text-sm font-regular text-gray-900">{formatCurrency(prevSalary.net_salary)}</td>
                        <td className="px-5 py-3 text-sm font-regular uppercase text-gray-900">{prevSalary.status}</td>
                        <td className="px-5 py-3 text-sm">
                          <Button
                            variant="link"
                            onClick={() => navigate(`/salaries/view/${prevSalary.salary_id}`)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden space-y-3">
                {displayedPreviousSalaries.map((prevSalary) => (
                  <div key={prevSalary.salary_id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-orange-500">
                    <div className="flex justify-between items-start gap-2 mb-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800">{prevSalary.month_year}</p>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{prevSalary.salary_id}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border whitespace-nowrap ${getStatusColor(prevSalary.status)}`}>
                          {prevSalary.status}
                        </span>
                        <RowActionsMenu
                          isOpen={activeDropdown === prevSalary.salary_id}
                          onToggle={() => toggleDropdown(prevSalary.salary_id)}
                          onClose={() => setActiveDropdown(null)}
                          menuRef={dropdownRef}
                          onView={() => navigate(`/salaries/view/${prevSalary.salary_id}`)}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Basic</p>
                        <p className="font-bold text-gray-900">{formatCurrency(prevSalary.basic_salary)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Allowances</p>
                        <p className="font-bold text-gray-900">{formatCurrency(prevSalary.allowances)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Deductions</p>
                        <p className="font-bold text-gray-900">{formatCurrency(prevSalary.deductions)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Net Salary</p>
                        <p className="font-bold text-gray-900">{formatCurrency(prevSalary.net_salary)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {previousSalaries.length > 3 && (
                <div className="mt-4 text-center">
                  <Button
                    variant="pagination"
                    onClick={() => setShowAllPrevious(!showAllPrevious)}
                  >
                    {showAllPrevious ? '↑ Show Less' : `↓ Show All (${previousSalaries.length - 3} more)`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
    </DashboardLayout>
  );
};

export default ViewSalary;